import React, { useState, useEffect } from 'react';
import { Box, Progress, useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import CustomerInfoStep from './steps/CustomerInfoStep';
import HouseInfoStep from './steps/HouseInfoStep';
import RoomInfoStep from './steps/RoomInfoStep';
import MechanicalSpaceStep from './steps/MechanicalSpaceStep';

// Define the fixed steps
const fixedSteps = [
  { title: 'Customer Info', component: CustomerInfoStep },
  { title: 'House Info', component: HouseInfoStep },
  // Room steps will be inserted dynamically here
  { title: 'Mechanical Space', component: MechanicalSpaceStep }, // This will be the last step
];

function WizardForm() {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [formData, setFormData] = useState(() => ({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    emailAddress: '',
    role: '',
    salesperson: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    altitude: '',
    projectType: '',
    projectGoal: '',
    installationTimeline: '',
    homePictures: [],
    electricalPanelPicture: [],
    mechanicalSpaceName: '',
    mechanicalSpaceDimensions: '',
    mechanicalSpaceTemperature: '',
    mechanicalSpaceVideo: [],
    mechanicalEquipmentPicture: [],
    rooms: [{
      roomName: '',
      roomDimensions: '',
      roomTemperature: '',
      fireplaceType: '',
      fireplaceVent: '',
      heatingCooling: '',
      otherAirSystems: '',
      roomVideo: [],
    }],
    submissionId: '',
  }));
  const [justAddedRoom, setJustAddedRoom] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  // Calculate the total number of steps including dynamic rooms
  // Fixed steps before rooms (2) + number of rooms + fixed steps after rooms (1)
  const totalSteps = 2 + formData.rooms.length + 1; // Corrected calculation

  // Determine the current step component based on the index
  const getCurrentStepComponent = () => {
    if (currentStepIndex < 2) {
      return fixedSteps[currentStepIndex].component;
    }
    if (currentStepIndex >= 2 && currentStepIndex < 2 + formData.rooms.length) {
      return RoomInfoStep;
    }
    if (currentStepIndex === 2 + formData.rooms.length) {
      return fixedSteps[fixedSteps.length - 1].component;
    }
    return null;
  };

  // Get the data for the current step
  const getCurrentStepData = () => {
    if (currentStepIndex < 2) {
      return formData;
    }
    if (currentStepIndex >= 2 && currentStepIndex < 2 + formData.rooms.length) {
      const roomIndex = currentStepIndex - 2;
      return formData.rooms[roomIndex] || {};
    }
    if (currentStepIndex === 2 + formData.rooms.length) {
      return formData;
    }
    return {};
  };

  // Function to update data for the current step/room without navigating
  const handleUpdateCurrentStepData = (stepData) => {
    setFormData((prev) => {
      if (currentStepIndex < 2) {
        return { ...prev, ...stepData };
      } else if (currentStepIndex >= 2 && currentStepIndex < 2 + prev.rooms.length) {
        const roomIndex = currentStepIndex - 2;
        const updatedRooms = [...prev.rooms];
        updatedRooms[roomIndex] = {
          ...updatedRooms[roomIndex],
          ...stepData,
        };
        return { ...prev, rooms: updatedRooms };
      } else if (currentStepIndex === 2 + prev.rooms.length) {
        return { ...prev, ...stepData };
      }
      return prev;
    });
  };

  // Modified handleNext - stores the latest step's data before moving forward
  const handleNext = (stepData = null) => {
    if (stepData) {
      handleUpdateCurrentStepData(stepData);
    }

    setTimeout(() => {
      if (currentStepIndex < totalSteps - 1) {
        setCurrentStepIndex(currentStepIndex + 1);
      } else {
        handleSubmit();
      }
    }, 0); // ensure setState completes before step transition
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  // Function to add another room
  const handleAddRoom = () => {
    const blankRoom = {
      roomName: '',
      roomDimensions: '',
      roomTemperature: '',
      roomVideo: [],
    };
    const updatedRooms = [...formData.rooms, blankRoom];
    setFormData({ ...formData, rooms: updatedRooms });
    setCurrentStepIndex(2 + updatedRooms.length - 1);
    setJustAddedRoom(true);
  };

  // Reset justAddedRoom after step changes
  useEffect(() => {
    if (justAddedRoom) setJustAddedRoom(false);
  }, [currentStepIndex, justAddedRoom]);

  // Ensure a unique submissionId is created early in the form process
  useEffect(() => {
    if (!formData.submissionId) {
      setFormData(prev => ({
        ...prev,
        submissionId: crypto.randomUUID()
      }));
    }
  }, [formData]);

  // Function to remove a room by index
  const handleRemoveRoom = (roomIndexToRemove) => {
    const updatedRooms = formData.rooms.filter((_, index) => index !== roomIndexToRemove);
    setFormData({ ...formData, rooms: updatedRooms });
    setCurrentStepIndex(currentStepIndex - 1);
    toast({
      title: 'Room Removed',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleSubmit = async () => {
    console.log('Final Form Data Submitted:', formData);
    console.log('Media fields:', {
      homePictures: formData.homePictures,
      electricalPanelPicture: formData.electricalPanelPicture,
      mechanicalEquipmentPicture: formData.mechanicalEquipmentPicture,
      mechanicalSpaceVideo: formData.mechanicalSpaceVideo,
      roomMedia: formData.rooms.map((r, i) => ({
        index: i,
        roomVideo: r.roomVideo
      }))
    });

    // Example of how you might structure data to send to Lambda:
    const dataToSend = {
      customerInfo: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        emailAddress: formData.emailAddress,
        role: formData.role,
        salesperson: formData.salesperson,
      },
      houseInfo: {
        streetAddress: formData.streetAddress,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        country: formData.country,
        altitude: formData.altitude,
        projectType: formData.projectType,
        projectGoal: formData.projectGoal,
        installationTimeline: formData.installationTimeline,
        homePictures: formData.homePictures,
        electricalPanelPicture: formData.electricalPanelPicture,
      },
      rooms: formData.rooms,
      mechanicalSpace: {
        mechanicalSpaceName: formData.mechanicalSpaceName,
        mechanicalSpaceDimensions: formData.mechanicalSpaceDimensions,
        mechanicalSpaceTemperature: formData.mechanicalSpaceTemperature,
        mechanicalSpaceVideo: formData.mechanicalSpaceVideo,
        mechanicalEquipmentPicture: formData.mechanicalEquipmentPicture,
      },
      submissionId: formData.submissionId,
    };

    console.log("Data to send to backend:", dataToSend);

    try {
      const apiResponse = await fetch(`${process.env.REACT_APP_API_URL}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });
      const result = await apiResponse.json();
      console.log('API Response:', result);
      toast({
        title: 'Form Submitted Successfully.',
        description: "We've received your information.",
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: 'Submission Failed.',
        description: error.message || 'There was an error submitting the form.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const CurrentStepComponent = getCurrentStepComponent();
  const currentStepData = getCurrentStepData();
  const isLastRoomStep = currentStepIndex === 2 + formData.rooms.length - 1 && formData.rooms.length > 0;
  const isRoomStep = currentStepIndex >= 2 && currentStepIndex < 2 + formData.rooms.length;
  const isMechanicalSpaceStep = currentStepIndex === totalSteps - 1;

  const progress = totalSteps > 1 ? (currentStepIndex / (totalSteps - 1)) * 100 : 0;

  return (
    <Box borderWidth="1px" rounded="lg" p={6} maxWidth="800px" mx="auto">
      {submitted ? (
        <Box textAlign="center" py={10} px={6}>
          <Box fontSize="3xl" mb={3} fontWeight="bold">Thank You!</Box>
          <Box fontSize="md" color="gray.600">
            Your form has been submitted successfully. We’ll be in touch soon.
          </Box>
          <Box fontSize="3xl" mt={4} fontWeight="bold">
            <a href="https://altitudecontrol.com" rel="noopener noreferrer" style={{ color: '#1d2b3f' }}>
              Altitude Control Technology
            </a>
          </Box>
        </Box>
      ) : (
        <>
          {totalSteps > 1 && <Progress value={progress} mb={6} hasStripe isAnimated />}
          {CurrentStepComponent && (
            <CurrentStepComponent
              formData={currentStepData}
              onNext={handleNext}
              onBack={handleBack}
              onUpdateStepData={handleUpdateCurrentStepData}
              isLastRoom={isLastRoomStep}
              onAddRoom={handleAddRoom}
              onNextStep={(data) => handleNext(data)}
              currentStepIndex={currentStepIndex}
              isRoomStep={isRoomStep}
              onRemoveRoom={handleRemoveRoom}
              isMechanicalSpaceStep={isMechanicalSpaceStep}
              onSubmit={handleSubmit}
              justAddedRoom={justAddedRoom}
              submissionId={formData.submissionId}
            />
          )}
        </>
      )}
    </Box>
  );
}

export default WizardForm;
